import i18n from "@plugins/i18n.ts";
import DOMUtils from "@utils/dom.utils";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import UgcFileService from "@plugins/richtext/service/ugc.file.service";
import MediaObjectService from "@plugins/richtext/service/media.object.service";
import MediaSettingsSubpage from "@/plugins/media/components/MediaSettingsSubpage.vue";
import DrawingCanvasModal from "@plugins/drawing/components/DrawingCanvasModal.vue";
import FileUploaderHidden from "@plugins/file-uploader-v2/components/FileUploaderHidden.vue";
import drawingEventBus from "@plugins/richtext/plugins/drawing/service/event-bus";
import { nextTick } from "vue";
import AppInstanceUtils from "@/plugins/common/service/app.instance.utils";
import { FileUploadEntry, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import { isDefined } from "@utils/var.check.ts";
import Compressor from "compressorjs";
import { MEDIA_TYPE } from "@/modules/editor/service/editor.constant";
import { MEDIA_RECORD_TYPE } from "@/plugins/media/media.constant";

interface MediaData {
  uuid: string;
  fileName?: string;
  isAddNewMedia: boolean;
}

interface CanvasConfig {
  eventBusWatchInstances: unknown[];
  imageUrl?: string;
  uuid?: string;
  fileName?: string;
  isNewImage?: boolean;
  createAsNew?: boolean;
  skipCropping?: boolean;
}

type MediaServiceType = MediaObjectService | UgcFileService | null;

export default function (editor) {
  const MEDIA_SELECTOR = ".sls-media[media-type='IMAGE'], .sls-media-object";

  const richTextEditor = editor.options.get("richTextEditor");
  const pluginEvent = editor.options.get("pluginEvent");
  let mediaService: MediaServiceType = null;
  let mediaUploadType = "";
  let drawingCanvasModalId: string | null = null;

  editor.ui.registry.addButton("drawing", {
    tooltip: i18n.t("tinymce_plugin_drawing_icon_button_tooltip"),
    icon: "color-picker",
    onAction: openCanvasModal,
  });

  (function init() {
    richTextEditor.plugins["drawing"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      openCanvasModal,
    };
    // set the editor context
    const context = richTextEditor.richTextContext || {};
    // UGC side has async component to create attempt for context.
    // Set up the file upload service
    if (context.pageDomain === "editor") {
      mediaService = new MediaObjectService(context);
      mediaUploadType = "mo";
    } else if (
      (context.pageDomain === "attempt" ||
        context.pageDomain === "forum" ||
        context.pageDomain === "notebook") &&
      richTextEditor.config &&
      richTextEditor.config.plugins &&
      richTextEditor.config.plugins.drawing
    ) {
      const config = richTextEditor.config.plugins.drawing.fileServiceConfig;
      mediaService = new UgcFileService(config);
      mediaUploadType = "ugc";
    }

    if (pluginEvent) {
      pluginEvent.$on("mediaRendered", enableDrawing);
    }
  })();

  function containPluginElements(container) {
    return container.querySelectorAll(".sls-media, .sls-media-object, figure.media").length > 0;
  }

  /**
   * Function to open the modal containing the drawing canvas
   */
  function openCanvasModal(config) {
    const canvasConfig: CanvasConfig = {};
    const eventBusWatchInstances = [];
    eventBusWatchInstances.push(drawingEventBus.$on("commitDrawing", handleCommitDrawing));
    eventBusWatchInstances.push(
      drawingEventBus.$on("hide", () => {
        window.tinyMCE.get(editor.id).focus();
      }),
    );
    canvasConfig.eventBusWatchInstances = eventBusWatchInstances;
    if (config && config.uuid) {
      // basic image information
      canvasConfig.imageUrl = config.imageUrl;
      canvasConfig.uuid = config.uuid;
      canvasConfig.fileName = config.fileName;

      // to indicate if image is newly uploaded.
      canvasConfig.isNewImage = isDefined(config.isNewImage) ? config.isNewImage : false;

      // If input media type not as the editor context type, then set flag to "create as new".
      // This will create a new media with the editor's context type.
      // E.g. media is MO, editor is UGC, drawing will be created as an UGC.
      canvasConfig.createAsNew = config.type !== mediaUploadType;
      canvasConfig.skipCropping = !!config.skipCropping;
    }

    drawingCanvasModalId = richTextEditor.$modal.show(DrawingCanvasModal, canvasConfig, {}, true);
  }

  const compressFiles = async (fileEntry: FileUploadEntry): Promise<FileUploadEntry> => {
    const compressOne = (file: File): Promise<File> =>
      new Promise((resolve, reject) => {
        // Get the original image size
        console.log("Image size before compress: " + file.size);
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          new Compressor(file, {
            maxWidth: img.width,
            maxHeight: img.height,
            mimeType: file.type,
            success: (result) => {
              URL.revokeObjectURL(url);
              resolve(result as File);
            },
            error: (err) => {
              URL.revokeObjectURL(url);
              reject(err);
            },
          });
        };
        img.onerror = (err) => {
          URL.revokeObjectURL(url);
          reject(err);
        };
        img.src = url;
      });

    try {
      fileEntry.file = await compressOne(fileEntry.file);
    } catch (error) {
      // Keep the original file on failure
      console.warn("Image compression failed, using original file:", error);
    }
    console.log("Image size after compress: " + fileEntry.file.size);
    return fileEntry;
  };

  /**
   * Function to handle when a drawing is added / edited.
   */
  async function handleCommitDrawing(drawingData) {
    const fileEntry = {
      file: drawingData.file,
    };

    const compressedEntry = await compressFiles(fileEntry as FileUploadEntry);

    // Step 1: Create the resource record first (UGC or MO) to get its uuid
    // before calling generate-scan-url, so Valkey has recordType+recordUuid.
    const mediaData: MediaData = await saveIfNotCreateMedia(drawingData);
    const recordType = mediaUploadType === "mo" ? MEDIA_RECORD_TYPE.MO : MEDIA_RECORD_TYPE.UGC;
    const fileUploaderModalId = richTextEditor.$modal.show(
      FileUploaderHidden,
      {
        uploadFlow: UploadFlow.IMG,
        fileEntry: compressedEntry,
        recordType: recordType,
        recordUuid: mediaData.uuid,
      },
      {
        "update:fileEntry": () => {
          // Resource record already created — insert into editor
          richTextEditor.attachedFiles.push({
            uuid: mediaData.uuid,
            file: drawingData.file,
          });
          richTextEditor.$modal.remove(fileUploaderModalId);
          if (drawingCanvasModalId) {
            richTextEditor.$modal.remove(drawingCanvasModalId);
          }
          nextTick(() => {
            richTextUtils.insertInlineAttachment(editor, {
              uuid: mediaData.uuid,
              mediaType: MEDIA_TYPE.IMAGE,
              fileName: mediaData.fileName ? mediaData.fileName : "drawing.png",
              type: mediaUploadType,
              isAddNewMedia: mediaData.isAddNewMedia,
            });

            const mediaEvents = {};
            if (mediaUploadType === "mo") mediaEvents.onMediaSettings = openSettings;
            mediaEvents.onCanvasDrawing = openCanvasModal;
            richTextUtils.renderInlineMedia(editor, ".inline-placeholder", mediaEvents);
            editor.save();
            enableDrawingOnMediaComponent(`.sls-media-tag[data-uuid='${mediaData.uuid}']`);
          });
        },
      },
      true,
    );
  }

  /**
   * Function to save (if existing) or pre create (if new) media from drawing.
   *
   * @param drawingData
   * @returns {Promise}
   */
  async function saveIfNotCreateMedia(drawingData): Promise<MediaData> {
    // if uuid exist, then save (update existing record). Otherwise create a new placeholder.
    const isSaveMedia = drawingData.uuid && !drawingData.createAsNew;
    if (!mediaService) {
      throw new Error("MediaService is not initialized");
    }
    if (isSaveMedia) {
      // Edit path: record already exists, uuid is known — no API call needed.
      // Just pass the existing uuid as resourceUuid into FileUploaderHidden.
      return {
        uuid: drawingData.uuid,
        isAddNewMedia: false,
      };
    }

    const uuid: string = await mediaService.preCreate();
    return {
      uuid: uuid,
      isAddNewMedia: true,
    };
  }

  /**
   * Function to support media settings.
   * @param settings
   */
  function openSettings(settings) {
    window.tinymce.currentEditor = editor;
    settings.editorSaveCallback = editorSaveCallback;
    richTextEditor.$contentSubpage.show(MediaSettingsSubpage, settings);
  }

  /**
   * Callback function for saving media settings.
   */
  function editorSaveCallback() {
    if (window.tinymce && window.tinymce.currentEditor) {
      window.tinymce.currentEditor.save();
      delete window.tinymce.currentEditor;
    }
  }

  /**
   * Function to enable drawing for Media component.
   * Expected selector:
   *  1. ".media-component" blanket selection of all existing Media components.
   *  2. ".sls-media-tag[data-uuid='<xxx>']" for targeted select
   * @param mediaSelector
   */
  function enableDrawingOnMediaComponent(mediaSelector) {
    const mediaComponentCounts = {};

    // enable drawing.
    DOMUtils.elOf(editor.getBody(), mediaSelector).forEach((element) => {
      if (DOMUtils.hasClass(element, "sls-media-tag")) {
        // case when specific media component is targetted.
        const wrapper = element.closest(".sls-media-wrapper");
        element = DOMUtils.elOf(wrapper, ".media-component")[0];
      }

      const mediaComponent = element.__vue__;

      if (mediaComponent && mediaComponent.mediatype === "image") {
        if (!mediaComponentCounts[mediaComponent.uuid]) {
          mediaComponentCounts[mediaComponent.uuid] = 0;
        }

        mediaComponentCounts[mediaComponent.uuid]++;

        // rerender MO to attach drawing event
        if (
          !mediaComponent.$attrs.onCanvasDrawing &&
          !mediaComponent.$?.vnode?.props?.onCanvasDrawing
        ) {
          handleRerender(mediaComponentCounts[mediaComponent.uuid] - 1, mediaComponent);
        }
        mediaComponent.$.exposed.enableDrawing();
      }
    });
  }

  function enableDrawing() {
    enableDrawingOnMediaComponent(".media-component");
  }

  /**
   * Function to support rendering to display mode in the editor.
   */
  function renderInEditor(rerender = false) {
    // render the media component if not already rendered.
    const mediaEvents = {};
    if (mediaUploadType === "mo") mediaEvents.onMediaSettings = openSettings;
    mediaEvents.onCanvasDrawing = openCanvasModal;
    richTextUtils.renderInlineMedia(editor, MEDIA_SELECTOR, mediaEvents, rerender);
    enableDrawingOnMediaComponent(".media-component");
  }

  /**
   * Function to support conversion of plugin element from display/edit format
   * to stripped down format for saving.
   * @param content
   * @returns {string}
   */
  function cleanUp(content) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".sls-media-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(containerAfterCleanUp, ".inline-placeholder").forEach((element) => {
      element.remove();
    });
    DOMUtils.elOf(container, ".sls-media-tag").forEach((element) => {
      const uuid = element.getAttribute("data-uuid");
      const media = DOMUtils.elOf(containerAfterCleanUp, `.sls-media-tag[data-uuid='${uuid}']`)[0];
      const mediaWrapper = media.closest(".sls-media-wrapper");

      // switch back the class
      DOMUtils.removeClass(element, "sls-media-tag");
      DOMUtils.addClass(element, "sls-media");
      mediaWrapper?.replaceWith(element);
    });

    DOMUtils.elOf(containerAfterCleanUp, "br[data-mce-bogus]").forEach((element) => {
      element.remove();
    });

    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpElement(element) {
    const mediaWrapper = element.closest(".sls-media-wrapper");

    // switch back the class
    DOMUtils.removeClass(element, "sls-media-tag");
    DOMUtils.addClass(element, "sls-media");
    mediaWrapper.replaceWith(element);
  }

  function handleRerender(index, mediaComponent) {
    const targetNode = DOMUtils.elOf(
      editor.getBody(),
      `.sls-media-tag[data-uuid='${mediaComponent.uuid}']`,
    )[index];
    if (!targetNode) return;
    cleanUpElement(targetNode);
    renderInEditor(true);
    AppInstanceUtils.unmountFromComponent(mediaComponent);
  }
}
